import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import {
  Modal,
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownMenuItem,
  StatusIndicator,
} from '@unified-checkout/design-system';
import { Check, Xmark } from '@unified-checkout/design-system/icons/index';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { MODAL_STEP_STATUS } from '@app/dashboard/constants/orders';
import { updateTerminalBranch, type UpdateTerminalBranchBody } from '@app/dashboard/services/inner_layout/terminals';
import type { UpdateTerminalBranchModalProps } from '@app/dashboard/types/innerLayout/terminals';
import styles from './update-terminal-branch-modal.module.scss';
import { THREE_SECONDS } from '@app/dashboard/constants/shared';

export const UpdateTerminalBranchModal = ({ 
  open, 
  onClose, 
  terminalId,
  terminalBranchId,
  isMobile = false,
  branches = [],
  isLoadingBranches = false
}: UpdateTerminalBranchModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedBranchId, setSelectedBranchId] = useState<string>('');
  const [modalStep, setModalStep] = useState<{
    type: string;
    message: string;
  }>({
    type: MODAL_STEP_STATUS.START,
    message: '',
  });

  useEffect(() => {
    if (open && terminalBranchId) {
      setSelectedBranchId(terminalBranchId);
    } else if (open) {
      setSelectedBranchId('');
    }
  }, [open, terminalBranchId]);

  const resetForm = () => {
    setSelectedBranchId('');
  };

  const handleClose = () => {
    resetForm();
    setModalStep({ type: MODAL_STEP_STATUS.START, message: '' });
    onClose();
  };

  const handleGoBackToForm = () => {
    setModalStep({ type: MODAL_STEP_STATUS.START, message: '' });
  };

  const {
    mutate: mutateUpdateTerminalBranch,
    isPending: isUpdating,
  } = useMutation({
    mutationFn: (body: UpdateTerminalBranchBody) => updateTerminalBranch(terminalId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_TERMINALS] });
      setModalStep({
        type: MODAL_STEP_STATUS.SUCCESS,
        message: t('innerLayout.devicesPage.modal.updateBranch.success'),
      });
      setTimeout(() => {
        handleClose();
      }, THREE_SECONDS);
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      setModalStep({
        type: MODAL_STEP_STATUS.FAILED,
        message: error.response?.data?.message || t('innerLayout.devicesPage.modal.updateBranch.error'),
      });
    },
  });

  const handleSubmit = () => {
    if (!selectedBranchId) {
      return;
    }

    mutateUpdateTerminalBranch({
      branch_id: selectedBranchId,
    });
  };

  const selectedBranch = branches.find((branch: any) => branch.branch_id === selectedBranchId);
  const hasBranchChanged = selectedBranchId !== terminalBranchId;

  const modalBtnsProps = [
    {
    text: t('innerLayout.update'),
    variant: 'contained' as const,
    color: 'primary' as const,
    onClickHandler: handleSubmit,
    loading: isUpdating,
    disabled: !selectedBranchId || isUpdating || !hasBranchChanged,
    },
    {
      text: t('innerLayout.cancel'),
      variant: 'outlined' as const,
      color: 'secondary' as const,
      onClickHandler: handleClose,
      disabled: isUpdating,
    }
  ];

  const errorModalBtnProps = [
    {
      text: t('innerLayout.goBack'),
      variant: 'outlined' as const,
      color: 'primary' as const,
      onClickHandler: handleGoBackToForm,
    },
    {
      text: t('innerLayout.close'),
      variant: 'contained' as const,
      color: 'primary' as const,
      onClickHandler: handleClose,
    },
  ];

  const renderModalContent = () => {
    if (modalStep.type === MODAL_STEP_STATUS.START) {
      return (
        <div className={styles['update-terminal-branch-modal__body']}>
          <Dropdown label={t('innerLayout.devicesPage.modal.updateBranch.branch')}>
            <DropdownButton
              type="input"
              placeholder={t('innerLayout.devicesPage.modal.updateBranch.branchPlaceholder')}
              text={selectedBranch ? selectedBranch.branch_name : ''}
              disabled={isLoadingBranches}
            />
            <DropdownMenu maxHeight="12rem">
              {branches.map((branch: any) => (
                <DropdownMenuItem
                  key={branch.branch_id}
                  text={branch.branch_name}
                  selected={selectedBranchId === branch.branch_id}
                  onClickHandler={() => setSelectedBranchId(branch.branch_id)}
                />
              ))}
            </DropdownMenu>
          </Dropdown>
        </div>
      );
    } else {
      const isSuccess = modalStep.type === MODAL_STEP_STATUS.SUCCESS;
      const isFailed = modalStep.type === MODAL_STEP_STATUS.FAILED;

      const title = isSuccess
        ? t('innerLayout.devicesPage.modal.updateBranch.successTitle')
        : isFailed
        ? t('innerLayout.devicesPage.modal.updateBranch.errorTitle')
        : undefined;

      const description = modalStep.message;

      const icon = isSuccess ? Check : Xmark;
      const status = isSuccess ? 'success' : 'error';

      return (
        <div className={styles['update-terminal-branch-modal__content']}>
          <StatusIndicator
            status={status}
            icon={icon}
            text={title}
            hintText={description}
          />
        </div>
      );
    }
  };

  const isSuccess = modalStep.type === MODAL_STEP_STATUS.SUCCESS;
  const isFailed = modalStep.type === MODAL_STEP_STATUS.FAILED;

  return (
    <Modal
      state={isSuccess ? 'success' : isFailed ? 'danger' : 'default'}
      open={open}
      onCloseHandler={handleClose}
      width={isMobile ? '90%' : '35rem'}
      position='center'
      isMobile={isMobile}
      title={
        modalStep.type === MODAL_STEP_STATUS.START
          ? t('innerLayout.devicesPage.modal.updateBranch.title')
          : undefined
      }
      btnsProps={
        modalStep.type === MODAL_STEP_STATUS.START
          ? modalBtnsProps
          : modalStep.type === MODAL_STEP_STATUS.SUCCESS
          ? undefined
          : errorModalBtnProps
      }
      hasCloseIcon={modalStep.type === MODAL_STEP_STATUS.START}
    >
      {renderModalContent()}
    </Modal>
  );
};
